import { buildSectionsFromModules } from "@/config/module-navigation";
import type { NavigationItem, NavigationSection } from "@repo/types";
import type { PlatformSubscriptionPlan } from "@repo/types";
import type { NormalizedUserModule } from "@repo/api-client";

type SubscriptionLike = {
  effective_status?: string | null;
  subscription_status?: string | null;
  review_status?: string | null;
  days_until_expiry?: number | null;
  subscription_expires_at?: string | null;
  plan_code?: string | null;
  current_plan_id?: string | null;
};

export const extractPlanModuleCodes = (plan?: Pick<PlatformSubscriptionPlan, "included_modules"> | null): string[] => {
  if (!plan || !plan.included_modules) {
    return [];
  }
  const included = plan.included_modules as unknown;
  if (Array.isArray(included)) {
    return included as string[];
  }
  if (typeof included === "object" && included !== null) {
    const modules = (included as { modules?: string[] }).modules;
    if (Array.isArray(modules)) {
      return modules;
    }
  }
  if (typeof included === "string" && included.length > 0) {
    try {
      const parsed = JSON.parse(included);
      return extractPlanModuleCodes({ included_modules: parsed });
    } catch {
      return [];
    }
  }
  return [];
};

export const buildEnabledModuleCodes = (userModules?: NormalizedUserModule[] | null): Set<string> | null => {
  if (!userModules || userModules.length === 0) return null;
  return new Set(
    userModules
      .filter((module) => module.is_enabled !== false)
      .map((module) => module.module_code)
  );
};

export const filterItemsByModules = (
  items: NavigationItem[],
  enabledModuleCodes: Set<string> | null
): NavigationItem[] => {
  if (!enabledModuleCodes) return items;
  return items
    .map((item) => {
      const requires = item.meta?.requiresModule;
      const requiredList = Array.isArray(requires) ? requires : requires ? [requires] : [];
      const allowed =
        item.id === "subscription" ||
        requiredList.length === 0 ||
        requiredList.some((code) => enabledModuleCodes.has(String(code)));
      if (!allowed) return null;
      const children = item.children ? filterItemsByModules(item.children, enabledModuleCodes) : undefined;
      return { ...item, children };
    })
    .filter(Boolean) as NavigationItem[];
};

export const isSubscriptionCurrentlyActive = (currentSubscription?: SubscriptionLike | null): boolean => {
  if (!currentSubscription) return false;
  const status = currentSubscription.effective_status || currentSubscription.subscription_status;

  if (status === "active") return true;

  if (status === "cancelled") {
    const daysUntilExpiry = currentSubscription.days_until_expiry;
    if (typeof daysUntilExpiry === "number" && daysUntilExpiry > 0) {
      return true;
    }
    const expiresAt = currentSubscription.subscription_expires_at;
    if (expiresAt && new Date(expiresAt) > new Date()) {
      return true;
    }
  }

  return false;
};

export const isSubscriptionExpiredOrTrialEligible = (
  currentSubscription?: SubscriptionLike | null,
  isSubscriptionActive?: boolean
): boolean => {
  if (!currentSubscription) return false;
  if (isSubscriptionActive) return false;

  const status = currentSubscription.effective_status || currentSubscription.subscription_status;
  const reviewStatus = currentSubscription.review_status;
  const trialEligibleStatuses = new Set([
    "expired",
    "cancelled",
    "suspended",
    "trial",
    "rejected",
    "pending",
    "pending_review",
    "awaiting_payment",
    "awaiting_confirmation",
  ]);

  return trialEligibleStatuses.has(status ?? "") || Boolean(reviewStatus && trialEligibleStatuses.has(reviewStatus));
};

export const buildModuleLookup = (userModules?: NormalizedUserModule[] | null): Map<string, NormalizedUserModule> => {
  const map = new Map<string, NormalizedUserModule>();
  (userModules ?? []).forEach((module) => map.set(module.module_code, module));
  return map;
};

export const buildNavigationFromModuleCodes = (
  moduleCodes: string[],
  moduleLookup: Map<string, NormalizedUserModule>,
  includeSubscription: boolean = true
): NavigationSection[] => {
  const sectionAccumulator = new Map<string, { order: number; label: string; items: NavigationItem[] }>();

  moduleCodes.forEach((moduleCode) => {
    const mod = moduleLookup.get(moduleCode);
    if (!mod) return;

    const sectionKey = mod.category || "others";
    const sectionLabel = mod.section_label || "其他模組";
    const entry = sectionAccumulator.get(sectionKey);

    const navItem: NavigationItem = {
      id: moduleCode,
      label: mod.name ?? moduleCode,
      href: moduleCode === "frontdesk" ? "/frontdesk-availability" : `/${moduleCode}`,
      description: mod.description,
      icon: mod.icon,
      meta: { sortOrder: mod.sort_order ?? 99 },
    };

    if (!entry) {
      sectionAccumulator.set(sectionKey, {
        order: mod.section_sort_order ?? mod.sort_order ?? 99,
        label: sectionLabel,
        items: [navItem],
      });
      return;
    }

    if (!entry.items.some((item) => item.id === navItem.id)) {
      entry.items.push(navItem);
    }
  });

  if (includeSubscription) {
    const subscriptionMod = moduleLookup.get("subscription");
    const subscriptionNavigationItem: NavigationItem = {
      id: "subscription",
      label: subscriptionMod?.name ?? "訂閱方案",
      href: "/subscription",
      description: subscriptionMod?.description,
      icon: subscriptionMod?.icon,
    };

    const brandsSection = sectionAccumulator.get("brands");
    if (brandsSection) {
      if (!brandsSection.items.some((item) => item.id === "subscription")) {
        brandsSection.items.push(subscriptionNavigationItem);
      }
    } else {
      sectionAccumulator.set("brands", {
        order: subscriptionMod?.section_sort_order ?? subscriptionMod?.sort_order ?? 99,
        label: subscriptionMod?.section_label ?? "品牌維護",
        items: [subscriptionNavigationItem],
      });
    }
  }

  return Array.from(sectionAccumulator.entries())
    .sort((a, b) => a[1].order - b[1].order)
    .map(([id, value]) => ({
      id,
      label: value.label,
      items: value.items.sort(
        (a, b) => ((a.meta?.sortOrder as number) ?? 99) - ((b.meta?.sortOrder as number) ?? 99)
      ),
    }));
};

export const resolveDynamicSections = ({
  variant,
  userModules,
  isStaffUser,
  isSubscriptionActive,
  isSubscriptionExpiredOrTrial,
  currentPlanModuleCodes,
  trialModuleCodes,
  moduleLookup,
  enabledModuleCodes,
}: {
  variant: "default" | "platform";
  userModules?: NormalizedUserModule[] | null;
  isStaffUser: boolean;
  isSubscriptionActive: boolean;
  isSubscriptionExpiredOrTrial: boolean;
  currentPlanModuleCodes?: string[] | null;
  trialModuleCodes?: string[] | null;
  moduleLookup: Map<string, NormalizedUserModule>;
  enabledModuleCodes: Set<string> | null;
}): NavigationSection[] | null => {
  if (variant !== "default") return null;

  if (!isStaffUser) {
    if (isSubscriptionActive && currentPlanModuleCodes && currentPlanModuleCodes.length > 0) {
      return buildNavigationFromModuleCodes(currentPlanModuleCodes, moduleLookup, true);
    }

    if (isSubscriptionExpiredOrTrial) {
      const effectiveTrialModuleCodes = trialModuleCodes ?? ["dashboard", "bookings", "services"];
      return buildNavigationFromModuleCodes(effectiveTrialModuleCodes, moduleLookup, true);
    }
  }

  if (!userModules || userModules.length === 0) {
    return null;
  }

  const sectionsFromModules = buildSectionsFromModules(userModules);
  const augmentedSections: NavigationSection[] = sectionsFromModules.map((section) => ({
    ...section,
    items: [...section.items],
  }));

  if (!isStaffUser) {
    const subscriptionMod = moduleLookup.get("subscription");
    const subscriptionNavigationItem: NavigationItem = {
      id: "subscription",
      label: subscriptionMod?.name ?? "訂閱方案",
      href: "/subscription",
      description: subscriptionMod?.description,
      icon: subscriptionMod?.icon,
    };

    const brandsSection = augmentedSections.find((section) => section.id === "brands");
    if (brandsSection) {
      if (!brandsSection.items.some((item) => item.id === "subscription")) {
        brandsSection.items.push(subscriptionNavigationItem);
      }
    } else {
      augmentedSections.push({
        id: "brands",
        label: subscriptionMod?.section_label ?? "品牌維護",
        items: [subscriptionNavigationItem],
      });
    }
  }

  return augmentedSections.map((section) => ({
    ...section,
    items: filterItemsByModules(section.items, enabledModuleCodes),
  }));
};

export const resolvePinnedSections = (
  sections: NavigationSection[] | undefined,
  enabledModuleCodes: Set<string> | null
): NavigationSection[] => {
  if (!sections) return [];
  return sections
    .map((section) => {
      const pinnedItems = section.items.filter((item) => {
        const meta = (item.meta ?? {}) as { alwaysVisible?: boolean };
        return Boolean(meta.alwaysVisible);
      });
      if (pinnedItems.length === 0) return null;
      return {
        ...section,
        items: filterItemsByModules(pinnedItems, enabledModuleCodes),
      };
    })
    .filter((section): section is NavigationSection => section !== null)
    .filter((section) => section.items.length > 0);
};

export const resolveMenuSections = ({
  sections,
  dynamicSections,
  pinnedSections,
  enabledModuleCodes,
}: {
  sections?: NavigationSection[];
  dynamicSections: NavigationSection[] | null;
  pinnedSections: NavigationSection[];
  enabledModuleCodes: Set<string> | null;
}): NavigationSection[] => {
  if (dynamicSections && dynamicSections.length > 0) {
    if (pinnedSections.length === 0) {
      return dynamicSections;
    }
    const merged = dynamicSections.map((section) => ({
      ...section,
      items: [...section.items],
    }));
    pinnedSections.forEach((pinnedSection) => {
      const existingIndex = merged.findIndex((section) => section.id === pinnedSection.id);
      if (existingIndex !== -1) {
        merged[existingIndex] = {
          ...merged[existingIndex],
          items: [...merged[existingIndex].items, ...pinnedSection.items],
        };
      } else {
        merged.push(pinnedSection);
      }
    });
    return merged;
  }

  return (sections ?? [])
    .map((section) => ({
      ...section,
      items: filterItemsByModules(section.items, enabledModuleCodes),
    }))
    .filter((section) => section.items.length > 0);
};
